import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';

import { Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  ColumnProps,
  DeleteDialog,
  LeoDatePicker,
  LeoSelectFieldMulti,
  Page,
  Table,
  useDialogState,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { FilterOptionModel, ReadingFailureReasonUnitPriceModel } from 'models';
import Yup from 'utils/yup';
import { stringify } from 'utils/base64';
import { getPageName } from 'utils/format';
import { SortDirection } from 'utils/arrays';
import { getValueSetValues } from 'utils/valueSetFunctions';
import { YEAR_MONTH_DATE_FORMAT, formatDate } from 'utils/dates';
import { useFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { RouteNames } from 'config/routeNames';
import { useCanEdit } from 'utils/auth';
import { useValueSetList } from 'features/valueSets';
import {
  useDeleteReadingFailureReasonUnitPrice,
  useExportReadingFailureReasonUnitPriceList,
  useReadingFailureReasonUnitPriceList,
} from 'features/readingFailureReasonUnitPrice';

import { ReactComponent as Export } from 'assets/icons/Material/fileDownload.svg';

import TariffsEditorDialog from './TariffsEditorDialog';

const PerformanceTariffsNotReadablePage: FC = () => {
  const {
    isOpen: isEditorOpen,
    openDialog: openEditor,
    closeDialog: closeEditor,
    selected: selectedEdit,
  } = useDialogState<ReadingFailureReasonUnitPriceModel>();

  const {
    isOpen: isDeleteOpen,
    openDialog: openDelete,
    closeDialog: closeDelete,
    selected: selectedDelete,
  } = useDialogState<ReadingFailureReasonUnitPriceModel>();

  const {
    onPageChange,
    onRowsPerPageChange,
    onLoadQuery,
    onFilter,
    onSort,
    params,
    apiParams,
    exportParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams({
    order: stringify([{ orderBy: 'key', order: SortDirection.ASC }]),
  });

  const {
    data: notReadableTariffsData,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useReadingFailureReasonUnitPriceList(apiParams);
  const { mutateAsync: deleteNotReadablePerformanceTariff } =
    useDeleteReadingFailureReasonUnitPrice();
  const { isLoading: isExportLoading, isRefetching, refetch } = useExportReadingFailureReasonUnitPriceList(exportParams);
  const { data: valueSetData } = useValueSetList({
    code: ['NLO'],
  });

  const { data: nloData, accessor: nloAccessor } = useFieldDataAndAccessor(
    getValueSetValues(valueSetData, 'NLO'),
    'id',
    'value',
    true,
    'key'
  );

  const visible = useCanEdit('TARIFF_READING_FAILURE_REASONS');

  const { activePanel, closePanel, setPanel } = usePanelState();

  const onDelete = async () => {
    if (selectedDelete?.id) {
      await deleteNotReadablePerformanceTariff(selectedDelete.id);
    }
  };

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.VALID_FROM',
      component: (
        <LeoDatePicker
          name="validFrom"
          label={<Trans i18nKey="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.VALID_FROM" />}
          isYearAndMonthOnly
        />
      ),
      divider: true,
      name: 'validFrom',
      panelFieldWidth: 12,
      panelFieldOffset: 12,
      abbrName: t('FILTERABBR.VALID_FROM'),
    },
    {
      name: 'readingFailureReasonIds',
      headerTitle: 'KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.READING_FAILURE_REASON',
      component: (
        <LeoSelectFieldMulti
          name="readingFailureReasonIds"
          data={nloData}
          accessor={nloAccessor}
          label="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.READING_FAILURE_REASON"
          readOnly
        />
      ),
      panelFieldWidth: 12,
    },
  ];

  const validationSchema = Yup.object().shape({
    validFrom: Yup.date().nullable(),
  });

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK),
    validationSchema,
    isFetching,
  });

  const notReadableTariffsColumns: ColumnProps<ReadingFailureReasonUnitPriceModel>[] = [
    {
      key: 'key',
      header: <Trans i18nKey="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.READING_FAILURE_REASON" />,
      accessor: (item: ReadingFailureReasonUnitPriceModel) =>
        item.readingFailureReason?.key || item.key,
      field: 'key',
      tooltip: (item: ReadingFailureReasonUnitPriceModel) =>
        item.readingFailureReason?.value || item.value,
      sortable: true,
    },
    {
      key: 'validFrom',
      header: <Trans i18nKey="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.VALID_FROM" />,
      accessor: (item: ReadingFailureReasonUnitPriceModel) =>
        formatDate(item.validFrom, YEAR_MONTH_DATE_FORMAT),
      field: 'validFrom',
      sortable: true,
    },
    {
      key: 'unitPriceForPower',
      header: <Trans i18nKey="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.UNIT_PRICE_FOR_POWER" />,
      accessor: 'unitPriceForPower',
      sortable: true,
    },
    {
      key: 'unitPriceForGas',
      header: <Trans i18nKey="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.UNIT_PRICE_FOR_GAS" />,
      accessor: 'unitPriceForGas',
      sortable: true,
    },
  ];

  const headerActions = [
    {
      name: 'export',
      label: 'COMMON.EXPORT',
      icon: <Export fill="currentColor" />,
      onClick: refetch,
      isLoading: isExportLoading || isRefetching,
      disabledIf: !exportParams?.filter,
    },
  ];

  const actions = [
    {
      name: 'edit',
      label: 'COMMON.EDIT',
      icon: <EditIcon fontSize="small" />,
      onClick: (tariff: ReadingFailureReasonUnitPriceModel) => openEditor(tariff),
      visible,
    },
    {
      name: 'delete',
      label: 'COMMON.DELETE',
      icon: <DeleteIcon fontSize="small" />,
      onClick: (tariff: ReadingFailureReasonUnitPriceModel) => openDelete(tariff),
      visible,
    },
  ];

  const panels = [filterConfig];

  return (
    <Page
      panels={panels}
      activePanel={activePanel}
      title={<Trans i18nKey="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.TITLE" />}
      inScroll
      closePanel={closePanel}
      setPanel={setPanel}
    >
      <Box>
        <Table
          filterDetailsData={{ nloAccessor }}
          panels={panels}
          activePanel={activePanel}
          setPanel={setPanel}
          onTableColumnVisibilityChange={onTableColumnVisibilityChange}
          list={notReadableTariffsData?.data || []}
          total={notReadableTariffsData?.meta.total}
          timestamp={dataUpdatedAt}
          enableTableView={true}
          loading={isLoading}
          params={params}
          onSort={onSort}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          columns={notReadableTariffsColumns}
          enableCheckbox={false}
          pageHeaderActions={headerActions}
          actions={visible ? actions : undefined}
          onAdd={
            visible ? (tariff: ReadingFailureReasonUnitPriceModel) => openEditor(tariff) : undefined
          }
          onLoadQuery={onLoadQuery}
        />
      </Box>
      <TariffsEditorDialog
        open={isEditorOpen}
        wageItemsData={nloData}
        wageItemsAccessor={nloAccessor}
        selected={selectedEdit}
        onClose={closeEditor}
      />
      <DeleteDialog
        open={isDeleteOpen}
        selected={selectedDelete}
        onClose={closeDelete}
        onSubmit={onDelete}
        deleteTitleAccessor="value"
      />
    </Page>
  );
};

export default PerformanceTariffsNotReadablePage;
